import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Client-side-available kill switches. Must stay in sync with the api registry
 * entries marked `clientSide: true` (apps/api/src/kill-switch/kill-switch.registry.ts).
 * Internal/server-only switches are deliberately NOT exposed here.
 */
export const KILL_SWITCH = {
  MAINTENANCE: 'ops.kill.global.maintenance',
  TRADING_ORDER_PLACEMENT: 'ops.kill.trading.orderPlacement',
  TRADING_AMEND: 'ops.kill.trading.amend',
  DEPOSITS: 'ops.kill.deposits',
  WITHDRAWAL_REQUEST: 'ops.kill.withdrawals.request',
  AUTH_SIGNUP: 'ops.kill.auth.signup',
} as const;

export type KillSwitchKey = (typeof KILL_SWITCH)[keyof typeof KILL_SWITCH];

/**
 * True when the capability is live (UX should allow it).
 *
 * Fails OPEN by design: if LaunchDarkly is unconfigured, still streaming, or the
 * flag isn't client-side-available, the value is absent and we do NOT block the
 * UI — the server-side guard is the authoritative gate (defense in depth). The
 * provider sets `reactOptions.useCamelCaseFlagKeys = false`, so flags are keyed
 * by their raw dotted key.
 */
export function useCapabilityEnabled(key: KillSwitchKey): boolean {
  const flags = useFlags();
  const value = flags[key];
  return typeof value === 'boolean' ? value : true;
}
